import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, Provider, inject } from '@angular/core';
import { EVENT_MANAGER_PLUGINS, EventManagerPlugin } from '@angular/platform-browser';

const PRESS_HOLD_DELAY_MS = 400;
const PRESS_MOVE_THRESHOLD_PX = 10;
const SUPPRESS_AFTER_RELEASE_DELAY_MS = 400;
const HAPTIC_VIBRATION_DURATION_MS = 15;

@Injectable()
export class PressEventPlugin extends EventManagerPlugin {
	readonly #ngZone = inject(NgZone);

	constructor() {
		super(inject(DOCUMENT));
	}

	override supports(eventName: string): boolean {
		return eventName === 'press' || eventName === 'pressup';
	}

	override addEventListener(element: HTMLElement, eventName: string, handler: Function): () => void {
		let timer: ReturnType<typeof setTimeout> | null = null;
		let startX = 0;
		let startY = 0;
		let isPressed = false;
		let suppressUntil = 0;

		const cleanupSession = () => {
			if (timer !== null) {
				clearTimeout(timer);
				timer = null;
			}
			window.removeEventListener('pointerup', onPointerUp);
			window.removeEventListener('pointercancel', onPointerCancel);
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('scroll', onScroll, true);
		};

		const preventSuppressed = (e: Event) => {
			if (isPressed || timer !== null || Date.now() < suppressUntil) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		const releasePress = (e: Event) => {
			if (isPressed) {
				suppressUntil = Date.now() + SUPPRESS_AFTER_RELEASE_DELAY_MS;
				isPressed = false;
				if (eventName === 'pressup') {
					this.#ngZone.run(() => handler(e));
				}
			}
		};

		const onPointerUp = (e: PointerEvent) => {
			if (e.pointerType !== 'touch') return;
			cleanupSession();
			releasePress(e);
		};

		const onPointerCancel = (e: PointerEvent) => {
			if (e.pointerType !== 'touch') return;
			cleanupSession();
			releasePress(e);
		};

		const onPointerMove = (e: PointerEvent) => {
			if (e.pointerType !== 'touch') return;
			if (Math.hypot(e.clientX - startX, e.clientY - startY) > PRESS_MOVE_THRESHOLD_PX) {
				cleanupSession();
				releasePress(e);
			}
		};

		const onScroll = () => {
			cleanupSession();
			releasePress(new Event('scroll'));
		};

		const onPointerDown = (e: PointerEvent) => {
			if (e.pointerType !== 'touch') return;

			cleanupSession();
			isPressed = false;
			startX = e.clientX;
			startY = e.clientY;

			timer = setTimeout(() => {
				timer = null;
				isPressed = true;

				if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
					try {
						navigator.vibrate(HAPTIC_VIBRATION_DURATION_MS);
					} catch {}
				}

				if (eventName === 'press') {
					this.#ngZone.run(() => handler(e));
				}
			}, PRESS_HOLD_DELAY_MS);

			window.addEventListener('pointerup', onPointerUp, { once: true });
			window.addEventListener('pointercancel', onPointerCancel, { once: true });
			window.addEventListener('pointermove', onPointerMove, { passive: true });
			window.addEventListener('scroll', onScroll, { once: true, capture: true });
		};

		element.addEventListener('pointerdown', onPointerDown);
		element.addEventListener('contextmenu', preventSuppressed, { capture: true });
		element.addEventListener('click', preventSuppressed, { capture: true });

		return () => {
			cleanupSession();
			element.removeEventListener('pointerdown', onPointerDown);
			element.removeEventListener('contextmenu', preventSuppressed, true);
			element.removeEventListener('click', preventSuppressed, true);
		};
	}
}

export function providePressEventPlugin(): Provider {
	return {
		provide: EVENT_MANAGER_PLUGINS,
		useClass: PressEventPlugin,
		multi: true,
	};
}
